import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Input, Button, message } from 'antd';
import { UserOutlined, MailOutlined, LockOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import axios from '../utils/axios';
import util from '../utils/util';
import flagImg from './assets/footer/flag.png';

export default function Register() {
    const location = useLocation();
    const history  = useHistory();
    const params   = new URLSearchParams(location.search);
    const token    = params.get('token') || '';
    const emailParam = params.get('email') || '';

    const [step,     setStep]     = useState('validating'); // validating | form | success | invalid
    const [survey,   setSurvey]   = useState('');
    const [fname,    setFname]    = useState('');
    const [uname,    setUname]    = useState('');
    const [password, setPassword] = useState('');
    const [confirm,  setConfirm]  = useState('');
    const [loading,  setLoading]  = useState(false);

    useEffect(() => {
        if (!token || !emailParam) { setStep('invalid'); return; }
        axios.get('survey/validate-invite', { params: { token } })
            .then(res => {
                if (res.data?.code === 409 || res.data?.existing_user) {
                message.warning('Account already exists. Please log in with your existing credentials.');
                setTimeout(() => history.push('/'), 2000);
                return;
            }
            if (res.data?.code === 200) {
                    setSurvey(res.data.survey_title || 'a survey');
                    setStep('form');
                } else {
                    setStep('invalid');
                }
            })
            .catch(() => setStep('invalid'));
    }, []); // eslint-disable-line

    const handleRegister = async () => {
        if (!fname) { message.error('Please enter your name'); return; }
        if (!password) { message.error('Please enter a password'); return; }
        if (password.length < 6) { message.error('Password must be at least 6 characters'); return; }
        if (password !== confirm) { message.error('Passwords do not match'); return; }
        setLoading(true);
        try {
            const res = await axios.post('survey/register-from-invite', {
                token, email: emailParam, fname, username: uname, password, confirm
            });
            if (res.data?.code === 409 || res.data?.existing_user) {
                message.warning('Account already exists. Please log in with your existing credentials.');
                setTimeout(() => history.push('/'), 2000);
                return;
            }
            if (res.data?.code === 200) {
                // Auto-login
                // Auto-login to get full nav and modules
                const loginRes = await axios.post('login', { username: res.data.username, password });
                const ld = loginRes.data;
                util.setLoginInfoLocalStorage(
                    ld.token, ld.name, ld.modules, ld.type,
                    ld.is_national, ld.is_client_admin, ld.is_admin, ld.client_id,
                    ld.permissions, ld.region, ld.nav || [], ld.regions,
                    ld.program_id, ld.account_type
                );
                localStorage.setItem('force_password_change', '0');
                localStorage.setItem('user_id', ld.user_id || res.data.user_id);
                message.success('Account created! Welcome to the QoC Platform.');
                setStep('success');
                setTimeout(() => history.push('/qoc/survey-builder'), 2000);
            } else {
                message.error(res.data?.message || 'Registration failed');
            }
        } catch(e) {
            message.error(e.response?.data?.message || e.message || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    const pwStrength = () => {
        if (!password) return null;
        if (password.length < 6) return { label:'Too short', color:'#ef4444', pct:20 };
        const score = [/[A-Z]/, /[a-z]/, /[0-9]/, /[^A-Za-z0-9]/].filter(r => r.test(password)).length;
        if (score === 4) return { label:'Strong', color:'#22c55e', pct:100 };
        if (score === 3) return { label:'Good', color:'#84cc16', pct:75 };
        if (score === 2) return { label:'Fair', color:'#eab308', pct:55 };
        return { label:'Weak', color:'#f97316', pct:35 };
    };
    const pw = pwStrength();

    return (
        <div style={{ minHeight:'100vh', background:'linear-gradient(135deg,#0d2137 0%,#1e3a5f 50%,#0d2137 100%)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
            <div style={{ width:'100%', maxWidth:440 }}>
                <div style={{ textAlign:'center', marginBottom:28 }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:12, marginBottom:12 }}>
                        <img src={flagImg} alt="Flag" style={{ height:36, borderRadius:4, boxShadow:'0 2px 8px rgba(0,0,0,0.3)' }}/>
                        <div>
                            <div style={{ color:'rgba(255,255,255,0.6)', fontSize:10, letterSpacing:2, textTransform:'uppercase' }}>Kingdom of Eswatini</div>
                            <div style={{ color:'#fff', fontSize:18, fontWeight:800 }}>Ministry of Health</div>
                            <div style={{ color:'rgba(255,255,255,0.6)', fontSize:11 }}>National Quality Management Programme</div>
                        </div>
                    </div>
                </div>

                <div style={{ background:'#fff', borderRadius:16, padding:'36px 32px', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>

                    {step === 'validating' && (
                        <div style={{ textAlign:'center', padding:'20px 0' }}>
                            <div style={{ fontSize:32, marginBottom:12 }}>🔐</div>
                            <div style={{ fontWeight:700, fontSize:16, color:'#1e293b' }}>Validating your invite...</div>
                        </div>
                    )}

                    {step === 'invalid' && (
                        <div style={{ textAlign:'center', padding:'20px 0' }}>
                            <CloseCircleOutlined style={{ fontSize:48, color:'#ef4444', display:'block', marginBottom:16 }}/>
                            <div style={{ fontWeight:700, fontSize:18, color:'#1e293b', marginBottom:8 }}>Invalid or Expired Invite</div>
                            <div style={{ color:'#64748b', fontSize:14, lineHeight:1.6, marginBottom:24 }}>
                                This invitation link is invalid or has expired. Please ask the survey owner to send a new invite.
                            </div>
                            <Button type="primary" size="large" style={{ width:'100%' }} onClick={() => history.push('/')}>
                                Back to Login
                            </Button>
                        </div>
                    )}

                    {step === 'form' && (
                        <>
                            <div style={{ marginBottom:24 }}>
                                <div style={{ fontWeight:800, fontSize:20, color:'#1e293b' }}>Create Your Account</div>
                                <div style={{ color:'#64748b', fontSize:13, marginTop:4 }}>
                                    You've been invited to collaborate on <strong>{survey}</strong>
                                </div>
                                <div style={{ background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:6, padding:'8px 12px', marginTop:10, fontSize:12, color:'#0369a1' }}>
                                    <MailOutlined style={{ marginRight:6 }}/> Registering as: <strong>{emailParam}</strong>
                                </div>
                            </div>

                            <div style={{ marginBottom:16 }}>
                                <label style={{ display:'block', fontWeight:600, fontSize:12, color:'#475569', marginBottom:6 }}>Full Name *</label>
                                <Input size="large" prefix={<UserOutlined style={{ color:'#94a3b8' }}/>}
                                    value={fname} onChange={e => setFname(e.target.value)}
                                    placeholder="Your full name" onPressEnter={handleRegister}/>
                            </div>

                            <div style={{ marginBottom:16 }}>
                                <label style={{ display:'block', fontWeight:600, fontSize:12, color:'#475569', marginBottom:6 }}>Username *</label>
                            <Input size="large" prefix={<UserOutlined style={{ color:'#94a3b8' }}/>}
                                value={uname} onChange={e => setUname(e.target.value)}
                                placeholder="Choose a username" onPressEnter={handleRegister}/>
                            </div>
                            <div style={{ marginBottom:16 }}>
                            <label style={{ display:'block', fontWeight:600, fontSize:12, color:'#475569', marginBottom:6 }}>Password *</label>
                                <Input.Password size="large" prefix={<LockOutlined style={{ color:'#94a3b8' }}/>}
                                    value={password} onChange={e => setPassword(e.target.value)}
                                    placeholder="Minimum 6 characters" onPressEnter={handleRegister}/>
                                {pw && (
                                    <div style={{ marginTop:6 }}>
                                        <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, marginBottom:3 }}>
                                            <span style={{ color:'#94a3b8' }}>Password strength</span>
                                            <span style={{ color: pw.color, fontWeight:700 }}>{pw.label}</span>
                                        </div>
                                        <div style={{ height:4, background:'#f1f5f9', borderRadius:2 }}>
                                            <div style={{ height:4, width: pw.pct+'%', background: pw.color, borderRadius:2, transition:'all 0.3s' }}/>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div style={{ marginBottom:24 }}>
                                <label style={{ display:'block', fontWeight:600, fontSize:12, color:'#475569', marginBottom:6 }}>Confirm Password *</label>
                                <Input.Password size="large" prefix={<LockOutlined style={{ color:'#94a3b8' }}/>}
                                    value={confirm} onChange={e => setConfirm(e.target.value)}
                                    placeholder="Re-enter your password" onPressEnter={handleRegister}
                                    status={confirm && confirm !== password ? 'error' : ''}/>
                                {confirm && confirm !== password && (
                                    <div style={{ fontSize:11, color:'#ef4444', marginTop:4 }}>Passwords do not match</div>
                                )}
                            </div>

                            <Button type="primary" size="large" loading={loading}
                                style={{ width:'100%', height:48, fontWeight:700, fontSize:15 }}
                                onClick={handleRegister}>
                                Create Account & Join Survey
                            </Button>

                            <div style={{ textAlign:'center', marginTop:16 }}>
                                <a onClick={() => history.push('/')} style={{ color:'#94a3b8', fontSize:12, cursor:'pointer' }}>
                                    Already have an account? Log in
                                </a>
                            </div>
                        </>
                    )}

                    {step === 'success' && (
                        <div style={{ textAlign:'center', padding:'20px 0' }}>
                            <CheckCircleOutlined style={{ fontSize:48, color:'#22c55e', display:'block', marginBottom:16 }}/>
                            <div style={{ fontWeight:700, fontSize:20, color:'#1e293b', marginBottom:8 }}>Welcome!</div>
                            <div style={{ color:'#64748b', fontSize:14, lineHeight:1.6 }}>
                                Your account has been created. Redirecting to Survey Builder...
                            </div>
                            <div style={{ marginTop:12, background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:8, padding:'10px 16px', fontSize:13 }}>
                                Your username: <strong>{uname}</strong>
                            </div>
                        </div>
                    )}
                </div>

                <div style={{ textAlign:'center', marginTop:16, color:'rgba(255,255,255,0.4)', fontSize:11 }}>
                    QoC Platform · Ministry of Health · Eswatini
                </div>
            </div>
        </div>
    );
}





